import { z } from 'zod';
/**
 * Le profil de facturation d'un espace (29/10/2026) : QUI l'espace paie au nom
 * de. Un espace = un payeur = une série de factures.
 *
 * Partagé par l'API et le portail : les mêmes refus, avec les mêmes mots, des
 * deux côtés.
 */
const texte = (max) => z.string().trim().max(max);
const facultatif = (max) => z
    .string()
    .trim()
    .max(max)
    .nullish()
    .transform((v) => (v ? v : null));
/**
 * Types d'adresse de facturation électronique proposés (BT-49-1, code EAS) —
 * ceux de l'annuaire français, plus le courriel.
 */
export const SCHEMES_ADRESSE_ELECTRONIQUE = ['0225', '0009', '0002', 'EM'];
/** Forme attendue de la valeur selon le type — les mêmes règles que SEmplyApp (`party-compliance.ts`). */
const FORMES_ADRESSE = {
    '0225': [/^\d{9}(_[A-Za-z0-9.-]+)*$/, 'un SIREN (9 chiffres), éventuellement suivi de « _SIRET » ou « _code »'],
    '0009': [/^\d{14}$/, 'un SIRET (14 chiffres)'],
    '0002': [/^\d{9}$/, 'un SIREN (9 chiffres)'],
    EM: [/^[^@\s]+@[^@\s]+\.[^@\s]+$/, 'une adresse courriel'],
};
/** Anomalie de l'adresse électronique, ou null si elle est conforme (ou absente). */
export function anomalieAdresseElectronique(scheme, valeur) {
    const v = (valeur ?? '').trim();
    if (!v)
        return null;
    if (!scheme || !SCHEMES_ADRESSE_ELECTRONIQUE.includes(scheme)) {
        return 'Choisissez le type d’adresse électronique.';
    }
    if (v.length > 255 || !/^[A-Za-z0-9._@+\-/:]+$/.test(v)) {
        return 'L’adresse électronique ne doit contenir ni espace ni caractère spécial.';
    }
    const [forme, libelle] = FORMES_ADRESSE[scheme];
    return forme.test(v) ? null : `Pour ce type, l’adresse électronique doit être ${libelle}.`;
}
export const profilFacturationSchema = z
    .object({
    type: z.enum(['PRO', 'PARTICULIER']),
    /** Dénomination ; pour un particulier, son nom complet. */
    name: texte(200).min(1, 'La dénomination est requise.'),
    firstName: facultatif(120),
    lastName: facultatif(120),
    /** 14 chiffres. Le SIREN en est tiré (ses 9 premiers chiffres). */
    siret: z
        .string()
        .trim()
        .transform((v) => v.replace(/[\s.]/g, ''))
        .pipe(z.string().regex(/^\d{14}$/, 'Le SIRET compte 14 chiffres.'))
        .nullish(),
    vatNumber: facultatif(20),
    addressLine1: texte(255).min(1, 'L’adresse est requise.'),
    addressLine2: facultatif(255),
    postCode: texte(10).min(2, 'Le code postal est requis.'),
    city: texte(120).min(1, 'La ville est requise.'),
    country: z
        .string()
        .trim()
        .toUpperCase()
        .regex(/^[A-Z]{2}$/, 'Code pays sur deux lettres (FR).')
        .default('FR'),
    email: z.string().trim().toLowerCase().email('Adresse e-mail invalide').max(255),
    /**
     * Adresse de facturation électronique (BT-49, 01/11/2026), facultative :
     * sans elle, la plateforme adresse la facture par le SIREN. Entreprise
     * seulement — sans objet pour un particulier.
     */
    adresseElectroniqueScheme: z
        .enum(SCHEMES_ADRESSE_ELECTRONIQUE)
        .nullish()
        .transform((v) => v ?? null),
    adresseElectroniqueValeur: facultatif(255),
    /** Autres destinataires de l'avis de facture (01/11/2026), cinq au plus. */
    emailsCopie: z
        .array(z.string().trim().toLowerCase().email('Adresse en copie invalide').max(255))
        .max(5, 'Cinq adresses en copie au plus.')
        .default([])
        .transform((liste) => [...new Set(liste)]),
})
    .superRefine((v, ctx) => {
    if (v.type === 'PRO' && !v.siret) {
        ctx.addIssue({ code: 'custom', path: ['siret'], message: 'Le SIRET est requis pour une entreprise.' });
    }
    if (v.type === 'PRO') {
        const anomalie = anomalieAdresseElectronique(v.adresseElectroniqueScheme, v.adresseElectroniqueValeur);
        if (anomalie)
            ctx.addIssue({ code: 'custom', path: ['adresseElectroniqueValeur'], message: anomalie });
    }
});
//# sourceMappingURL=facturation.js.map